/**
 * User Management › Graders (ops mock version 13, §B4; Jeff, 28 October 2026) — everyone holding the Grader role,
 * across every zone.
 *
 * READS S9's grader list through its own API (`GET /admin/grading/graders`, read-only) and changes nothing of S9's:
 * pause and resume go through the person-centric access write (ADR-113 D25 — `PATCH /admin/people/{sub}/access/
 * operator/nexa-grader-programme`), the same verb the access panel offers on the Grader row. Removal and reinstatement
 * with a reason stay on the access panel, which the eye opens.
 *
 * ⚠ NO ADD GRADER FORM. An invited grader is appointed with per-grader commercial terms — paid or unpaid — and that
 * arrives with `s9-grader-invite-paid-pool`; the server refuses a grader appointment until then
 * (`grader_appointment_not_built`), so a form here would be a button whose only outcome is an error.
 */
import { useMemo, useState } from 'react';
import { FilterPills } from '../../../components/FilterPills';
import { FilterSearch } from '../../../components/FilterSearch';
import { ColumnTooltip } from '../../../components/ColumnTooltip';
import { ZoneBoardLegend } from '../../../components/ZoneBoardLegend';
import { ZoneCell, ZoneFanoutWarning } from '../../../components/ZoneIndicators';
import { useZoneReadReport } from '../../../components/ZoneContext';
import { useToast } from '../../../components/ToastProvider';
import { useReAuth, ReauthCancelledError } from '../../../lib/reauth';
import { actionErrorToast } from '../../../lib/friendly-error';
import { ZONES, consoleZoneLabel, zoneByCode, type PortalZone } from '../../../lib/zones';
import { ALL_ZONES, countInZone, matchesZoneFilter, zoneBoardLegend } from '../../../lib/zone-board';
import { listGraders, type GraderRow, type Pool } from '../../../lib/grading-api';
import { changeAccessEntry, GRADER_ORG_ID } from '../../../lib/person-access-api';
import { ActionConfirmModal } from '../ActionConfirmModal';
import { ActionIcon, LoadFailed, Loading, PersonEye, fmtDate, useZoneList, type OpenPerson } from './shared';

type StatusKey = 'active' | 'paused' | 'removed';
const statusKey = (g: GraderRow): StatusKey => (g.status === 'suspended' ? 'paused' : g.status === 'removed' ? 'removed' : 'active');
const STATUS_BADGE: Record<StatusKey, [string, string]> = {
  active: ['portal-badge-active', 'Active'], paused: ['portal-badge-paused', 'Paused'], removed: ['portal-badge-removed', 'Removed'],
};
const TYPE_LABEL: Record<Pool, string> = { invited: 'Invited', professional: 'Industry professional', community: 'Community' };
const ALL_STATUSES: StatusKey[] = ['active', 'paused', 'removed'];

const readGraders = async (z: PortalZone): Promise<GraderRow[]> => (await listGraders(z)).graders;

export function GradersTab({ onOpenPerson }: { onOpenPerson: OpenPerson }) {
  const toast = useToast();
  const reauth = useReAuth();
  const { rows, results, failed, failedZones, reload } = useZoneList<GraderRow>(readGraders, 'the graders');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<StatusKey>>(new Set(ALL_STATUSES));
  const [zoneFilter, setZoneFilter] = useState(ALL_ZONES);
  const [type, setType] = useState<'all' | Pool>('all');
  const [confirm, setConfirm] = useState<{ row: GraderRow & { zone: string }; op: 'suspend' | 'reactivate' } | null>(null);
  const [busy, setBusy] = useState(false);

  const all = useMemo(() => rows ?? [], [rows]);
  useZoneReadReport({ rows, failedZones, filter: zoneFilter });
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((g) => selected.has(statusKey(g)) && matchesZoneFilter(g, zoneFilter)
      && (type === 'all' || g.pool === type)
      && (!q || `${g.name ?? ''} ${g.graderNumber} ${g.email ?? ''}`.toLowerCase().includes(q)));
  }, [all, selected, zoneFilter, type, search]);
  const count = (k: StatusKey) => all.filter((g) => statusKey(g) === k).length;
  // A grader is known by name, else email, else the pool's number; a profile not written yet has neither number nor name.
  const nameOf = (g: GraderRow) => g.name || g.email || (g.graderNumber ? `Grader ${g.graderNumber}` : 'this grader');

  const run = async () => {
    if (!confirm) return;
    const { row, op } = confirm;
    const zone = zoneByCode(row.zone);
    setBusy(true);
    try {
      await reauth.withReauth(() => changeAccessEntry(row.sub, { kind: 'operator', id: GRADER_ORG_ID }, op, zone));
      toast.push({
        variant: 'success',
        title: op === 'suspend' ? 'Grader paused' : 'Grader resumed',
        detail: op === 'suspend'
          ? `${nameOf(row)} is offered no sites while paused; their grades so far stand.`
          : `${nameOf(row)} is offered sites again.`,
      });
      setConfirm(null);
      void reload();
    } catch (err) {
      if (err instanceof ReauthCancelledError) return;
      toast.push({ variant: 'error', ...actionErrorToast(err, {
        action: op === 'suspend' ? 'Pause grader' : 'Resume grader', subject: nameOf(row), zoneLabel: consoleZoneLabel(row.zone),
      }) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="users-tab-panel" aria-label="Graders" data-testid="users-tab-graders">
      <h2 className="users-tab-heading">
        Graders
        {rows && <span className="portal-badge">{all.length} · {count('active')} active · {count('paused')} paused · {count('removed')} removed</span>}
      </h2>
      <p className="panel-help">
        Everyone holding the <code>grader</code> role, in the zone their sign-in was born in — <b>community</b> graders
        (subscribers who joined from the app), <b>invited</b> graders and <b>industry professionals</b>. Pause and resume
        here; the eye opens the person&rsquo;s access panel, where the role is removed or reinstated with a reason.
        Inviting graders arrives with the paid-grader pool (per-grader terms, paid or unpaid).
      </p>

      <div className="portal-applications-controls">
        <FilterSearch value={search} onChange={setSearch} placeholder="Search name, grader number or email…" ariaLabel="Search graders by name, number or email" />
        <FilterPills
          ariaLabel="Filter graders by status"
          selected={selected}
          onChange={setSelected}
          groups={[
            { key: 'active', label: 'Active', icon: '✅', count: count('active') },
            { key: 'paused', label: 'Paused', icon: '⏸️', count: count('paused') },
            { key: 'removed', label: 'Removed', icon: '⛔', count: count('removed') },
          ]}
        />
      </div>
      <div className="portal-users-filters">
        <label className="portal-users-filter-label">
          Zone
          <select className="portal-select" aria-label="Zone filter" value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)}>
            <option value={ALL_ZONES}>All zones</option>
            {ZONES.map((z) => <option key={z.code} value={z.code}>{z.label}</option>)}
          </select>
        </label>
        <label className="portal-users-filter-label">
          Grader type
          <select className="portal-select" value={type} onChange={(e) => setType(e.target.value as 'all' | Pool)}>
            <option value="all">All types</option>
            <option value="community">Community</option>
            <option value="invited">Invited</option>
            <option value="professional">Industry professional</option>
          </select>
        </label>
      </div>
      <ZoneFanoutWarning failedZones={failedZones} noun="graders" onRetry={() => void reload()} />
      <ZoneBoardLegend
        testId="users-graders-legend"
        ariaLabel="Graders by zone"
        loading={rows === null}
        columns={[{ key: 'all', label: 'Graders' }, { key: 'active', label: 'Active' }, { key: 'paused', label: 'Paused' }, { key: 'removed', label: 'Removed' }]}
        rows={zoneBoardLegend(results, {
          counts: (code) => ({
            all: countInZone(all, code),
            active: countInZone(all, code, (g) => statusKey(g) === 'active'),
            paused: countInZone(all, code, (g) => statusKey(g) === 'paused'),
            removed: countInZone(all, code, (g) => statusKey(g) === 'removed'),
          }),
        })}
      />
      <p className="legend-cap">Counted over every row, whatever the filter shows.</p>

      {rows === null && <Loading text="Loading the graders…" />}
      {failed && <LoadFailed what="the graders" onRetry={() => void reload()} />}
      {rows !== null && !failed && (
        <table className="portal-users-table" data-testid="users-graders-table">
          <thead>
            <tr>
              <th scope="col">Grader</th>
              <th scope="col">Email</th>
              <th scope="col">
                <span className="portal-col-help-wrap">Grader type
                  <ColumnTooltip columnName="Grader type" items={[
                    { term: 'Community', desc: 'A subscriber who became a grader from the app; five sites a sitting.' },
                    { term: 'Invited', desc: 'Appointed by Nexanomics into the paid-grader pool.' },
                    { term: 'Industry professional', desc: 'A consultant or advocate; sites they hold or referred are excluded.' },
                  ]} />
                </span>
              </th>
              <th scope="col">Zone</th>
              <th scope="col">
                <span className="portal-col-help-wrap">Status
                  <ColumnTooltip columnName="Status" items={[
                    { term: 'Active', desc: 'Holds the Grader role and is offered sites.' },
                    { term: 'Paused', desc: 'Offered no sites while paused; grades so far stand.' },
                    { term: 'Removed', desc: 'Removed with a reason on the access panel; reinstated there.' },
                  ]} />
                </span>
              </th>
              <th scope="col">Sites graded</th>
              <th scope="col">Last sitting</th>
              <th scope="col">Since</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={9} className="portal-users-empty">
                {selected.size === 0 ? 'No filter pills selected — click a status pill to show graders.' : 'No graders match the current filters.'}
              </td></tr>
            )}
            {shown.map((g) => {
              const sk = statusKey(g);
              const [cls, word] = STATUS_BADGE[sk];
              return (
                <tr key={`${g.zone}:${g.sub}`} data-status={sk}>
                  <td>
                    {/* The name (s1-launchpad-under-2s, 30 October 2026); the number is shown only when there is no name. */}
                    <div className="portal-users-name" data-testid="grader-name">{g.name || (g.graderNumber ? `Grader ${g.graderNumber}` : '—')}</div>
                  </td>
                  <td>{g.email ?? '—'}</td>
                  <td>{TYPE_LABEL[g.pool]}</td>
                  <td><ZoneCell zone={g.zone} /></td>
                  <td><span className={`portal-badge ${cls}`}>{word}</span></td>
                  <td className="num">{g.sitesGraded}</td>
                  <td className="portal-users-created" data-testid="grader-last-sitting">{g.lastSittingAt ? fmtDate(g.lastSittingAt) : '—'}</td>
                  <td className="portal-users-created">{fmtDate(g.grantedAt)}</td>
                  <td className="portal-users-row-actions portal-actions-col">
                    <div className="portal-actions-row">
                      <PersonEye name={nameOf(g)} onOpen={() => onOpenPerson(g.sub, g.zone)} />
                      {sk === 'active' && (
                        <ActionIcon act="pause" icon="pause" label={`Pause ${nameOf(g)}`} tip="Pause — no sites offered; grades so far stand"
                          onClick={() => setConfirm({ row: g, op: 'suspend' })} />
                      )}
                      {sk === 'paused' && (
                        <ActionIcon act="resume" icon="resume" label={`Resume ${nameOf(g)}`} tip="Resume — sites offered again"
                          onClick={() => setConfirm({ row: g, op: 'reactivate' })} />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <ActionConfirmModal
        open={confirm !== null}
        title={confirm?.op === 'suspend' ? 'Pause this grader?' : 'Resume this grader?'}
        intent={confirm?.op === 'suspend' ? 'warning' : 'primary'}
        primaryLabel={confirm?.op === 'suspend' ? 'Pause' : 'Resume'}
        loading={busy}
        onConfirm={() => void run()}
        onCancel={() => setConfirm(null)}
      >
        {confirm && (confirm.op === 'suspend'
          ? <p><b>{nameOf(confirm.row)}</b> keeps the Grader role but is offered no sites while paused. Their grades so far stand. Resume any time from this table.</p>
          : <p><b>{nameOf(confirm.row)}</b> is offered sites again from the next offer.</p>)}
      </ActionConfirmModal>
    </section>
  );
}
